// Part geometry in millimetres. World is y-up. Every part has a local frame centred on
// `pos`: x = length (grain), y = width, z = thickness (rods: x = axis, D = diameter).
//
// A board is a hexahedron bounded by six planes in its local frame, in the order
// x-, x+, y-, y+, z-, z+ (FACE). Inside means n·p ≤ d. A square board is the plain box;
// end cuts (Gehrung, Neigung), edge bevels and joints (tenon shoulders, miters, a longer
// end for a housing) only move or tilt these planes, so the solid always stays a convex
// hexahedron whose 8 corners are the intersections of three planes.

import { Vector3, Matrix3, Matrix4, Quaternion, Euler } from 'three'

export const FACE = ['x-', 'x+', 'y-', 'y+', 'z-', 'z+']
export const FACE_LABEL = { 'x-': 'Ende A', 'x+': 'Ende B', 'y-': 'Kante 1', 'y+': 'Kante 2', 'z-': 'Fläche 1', 'z+': 'Fläche 2' }
const AXIS_OF = [0, 0, 1, 1, 2, 2]
const SIGN_OF = [-1, 1, -1, 1, -1, 1]
const AXES = [new Vector3(1, 0, 0), new Vector3(0, 1, 0), new Vector3(0, 0, 1)]

// Corner c = i + 2j + 4k (i: x side, j: y side, k: z side); each face is a cyclic quad.
export const FACE_QUADS = [
  [0, 2, 6, 4], // x-
  [1, 5, 7, 3], // x+
  [0, 4, 5, 1], // y-
  [2, 3, 7, 6], // y+
  [0, 1, 3, 2], // z-
  [4, 6, 7, 5], // z+
]

const RAD = Math.PI / 180
export const deg = (r) => r / RAD
export const rad = (d) => d * RAD

/** World matrix of a part (rotation in degrees, Euler XYZ). */
export function partMatrix(part) {
  const q = new Quaternion().setFromEuler(new Euler(rad(part.rot?.[0] || 0), rad(part.rot?.[1] || 0), rad(part.rot?.[2] || 0), 'XYZ'))
  return new Matrix4().compose(new Vector3(...(part.pos || [0, 0, 0])), q, new Vector3(1, 1, 1))
}

/** Bounding box of a profile part's outline (local x/y). */
export function profileBox(part) {
  const xs = part.outline.map((p) => p[0]), ys = part.outline.map((p) => p[1])
  return { x0: Math.min(...xs), x1: Math.max(...xs), y0: Math.min(...ys), y1: Math.max(...ys) }
}

export function sizeOf(part) {
  if (part.type === 'profile') { const b = profileBox(part); return [b.x1 - b.x0, b.y1 - b.y0, part.T] }
  if (part.type === 'rod' || isRoundHw(part)) return [part.L, part.D, part.D]
  if (part.type === 'hw' && part.hw === 'birne') return [part.D, part.D, part.D]
  return [part.L, part.W, part.T]
}

export const isRoundHw = (p) => p.type === 'hw' && ['fassung', 'kabel', 'baldachin', 'diffusor', 'led'].includes(p.hw)
/** Bought parts that give light (glow and a light source when "Licht an"). */
export const isLight = (p) => p.type === 'hw' && ['birne', 'diffusor', 'led', 'ledstrip', 'platte'].includes(p.hw)
export const isRound = (p) => p.type === 'rod' || isRoundHw(p)

/** The six plain box planes of a part, before any cut. */
export function boxPlanes([L, W, T]) {
  const half = [L / 2, W / 2, T / 2]
  return FACE.map((_, f) => ({ n: AXES[AXIS_OF[f]].clone().multiplyScalar(SIGN_OF[f]), d: half[AXIS_OF[f]] }))
}

/**
 * Places a tilted plane through the innermost corner of the face it replaces: the cut
 * only removes wood, and the given length stays the long point.
 */
function pivotPlane(n, half, f) {
  const ax = AXIS_OF[f], sg = SIGN_OF[f]
  let d = Infinity
  for (let c = 0; c < 8; c++) {
    const p = new Vector3(c & 1 ? half[0] : -half[0], c & 2 ? half[1] : -half[1], c & 4 ? half[2] : -half[2])
    if (Math.sign(p.getComponent(ax)) !== sg) continue
    d = Math.min(d, n.dot(p))
  }
  return d
}

/**
 * Planes of a board with its own cuts (no joints yet).
 *  ends.a / ends.b: { miter, bevel } in degrees. miter tilts the end in the width
 *    (positive: the Kante-1 edge is the long point), bevel tilts it in the thickness
 *    (positive: Fläche 1 is the long point).
 *  edges.y0 / edges.y1: bevel of the long edges in degrees (positive: Fläche 2 keeps
 *    its full width, Fläche 1 gets narrower) — for polygon shades, mitred boxes.
 */
export function cutPlanes(part) {
  if (part.type === 'profile') {
    // A free outline (laser part): its planes are the outline's bounding box, which need
    // not be centred on the part's origin (a half ring keeps the ring's centre as origin).
    const b = profileBox(part)
    const d = [-b.x0, b.x1, -b.y0, b.y1, part.T / 2, part.T / 2]
    return FACE.map((_, f) => ({ n: AXES[AXIS_OF[f]].clone().multiplyScalar(SIGN_OF[f]), d: d[f] }))
  }
  const size = sizeOf(part)
  const half = size.map((s) => s / 2)
  const planes = boxPlanes(size)
  if (part.type !== 'board' && !(part.type === 'hw' && part.hw === 'box')) return planes
  const end = (f, cut) => {
    if (!cut || (!cut.miter && !cut.bevel)) return
    const sx = SIGN_OF[f]
    const m = rad(cut.miter || 0), b = rad(cut.bevel || 0)
    // Tilting toward +y / +z keeps the Kante-1 / Fläche-1 side long.
    const n = new Vector3(sx * Math.cos(m) * Math.cos(b), Math.sin(m) * Math.cos(b), Math.sin(b)).normalize()
    planes[f] = { n, d: pivotPlane(n, half, f) }
  }
  end(0, part.ends?.a)
  end(1, part.ends?.b)
  const edge = (f, a) => {
    if (!a) return
    const sy = SIGN_OF[f]
    const e = rad(a)
    // Tilting toward -z keeps Fläche 2 at full width, Fläche 1 gets narrower.
    const n = new Vector3(0, sy * Math.cos(e), -Math.sin(e)).normalize()
    planes[f] = { n, d: pivotPlane(n, half, f) }
  }
  edge(2, part.edges?.y0)
  edge(3, part.edges?.y1)
  return planes
}

const _m3 = new Matrix3()
/** Intersection point of three planes, or null when two are parallel. */
export function intersect3(p1, p2, p3) {
  const a = p1.n, b = p2.n, c = p3.n
  _m3.set(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z)
  const det = _m3.determinant()
  if (Math.abs(det) < 1e-9) return null
  _m3.invert()
  return new Vector3(p1.d, p2.d, p3.d).applyMatrix3(_m3)
}

/** The 8 corners (local) of the hexahedron bounded by six planes (order of FACE). */
export function hexaCorners(planes) {
  const out = []
  for (let c = 0; c < 8; c++) {
    const p = intersect3(planes[c & 1 ? 1 : 0], planes[c & 2 ? 3 : 2], planes[c & 4 ? 5 : 4])
    out.push(p || new Vector3())
  }
  return out
}

export function transformPlane(plane, m) {
  // m is rigid (rotation + translation): n' = R n, d' = d + n'·t
  const n = plane.n.clone().transformDirection(m)
  const t = new Vector3().setFromMatrixPosition(m)
  return { n, d: plane.d + n.dot(t) }
}

/** World plane → local plane of a part (inverse of its rigid matrix). */
export function toLocalPlane(plane, partM) {
  return transformPlane(plane, partM.clone().invert())
}

export function aabbOf(points) {
  const min = new Vector3(Infinity, Infinity, Infinity), max = new Vector3(-Infinity, -Infinity, -Infinity)
  for (const p of points) { min.min(p); max.max(p) }
  return { min, max, size: max.clone().sub(min) }
}

/** World axes of a part's local x, y, z. */
export function axesOf(m) {
  const e = m.elements
  return [new Vector3(e[0], e[1], e[2]), new Vector3(e[4], e[5], e[6]), new Vector3(e[8], e[9], e[10])]
}

// ── Convex polygons in a plane ─────────────────────────────────────────────

export function planeBasis(n) {
  const a = Math.abs(n.x) < 0.9 ? new Vector3(1, 0, 0) : new Vector3(0, 1, 0)
  const u = a.sub(n.clone().multiplyScalar(a.dot(n))).normalize()
  const v = new Vector3().crossVectors(n, u)
  return [u, v]
}

const area2 = (poly) => {
  let s = 0
  for (let i = 0; i < poly.length; i++) { const a = poly[i], b = poly[(i + 1) % poly.length]; s += a[0] * b[1] - b[0] * a[1] }
  return s / 2
}
export const polyArea = (poly) => Math.abs(area2(poly))
const ccw = (poly) => (area2(poly) < 0 ? [...poly].reverse() : poly)

/** Sutherland–Hodgman: intersection of two convex 2D polygons. */
export function clipConvex(subject, clip) {
  let out = ccw(subject)
  const c = ccw(clip)
  for (let i = 0; i < c.length && out.length; i++) {
    const a = c[i], b = c[(i + 1) % c.length]
    const inside = (p) => (b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0]) >= -1e-7
    const cross = (p, q) => {
      const x1 = a[0], y1 = a[1], x2 = b[0], y2 = b[1], x3 = p[0], y3 = p[1], x4 = q[0], y4 = q[1]
      const den = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4)
      const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / den
      return [x1 + t * (x2 - x1), y1 + t * (y2 - y1)]
    }
    const inp = out
    out = []
    for (let j = 0; j < inp.length; j++) {
      const p = inp[j], q = inp[(j + 1) % inp.length]
      const pi = inside(p), qi = inside(q)
      if (pi) out.push(p)
      if (pi !== qi) out.push(cross(p, q))
    }
  }
  return out
}

/** Circle as a polygon (for round ends). */
export function circlePoly(center, axis, r, n = 24) {
  const [u, v] = planeBasis(axis)
  const out = []
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2
    out.push(center.clone().addScaledVector(u, Math.cos(a) * r).addScaledVector(v, Math.sin(a) * r))
  }
  return out
}

export { Vector3, Matrix4, Quaternion, Euler }

const AXV = { '+x': [1, 0, 0], '-x': [-1, 0, 0], '+y': [0, 1, 0], '-y': [0, -1, 0], '+z': [0, 0, 1], '-z': [0, 0, -1] }
const r3 = (x) => { const d = Math.round(deg(x) * 1000) / 1000; return Object.is(d, -0) ? 0 : d }

/** Euler angles (degrees, XYZ) whose local x/y axes point along the world vectors a/b. */
export function rotFromAxes(a, b) {
  const x = new Vector3(...a).normalize()
  const y = new Vector3(...b).addScaledVector(x, -new Vector3(...b).dot(x)).normalize()
  const z = new Vector3().crossVectors(x, y)
  const e = new Euler().setFromRotationMatrix(new Matrix4().makeBasis(x, y, z), 'XYZ')
  return [r3(e.x), r3(e.y), r3(e.z)]
}

/**
 * Euler angles (degrees, XYZ) that turn the local length axis to `lx` and the width axis
 * to `ly` ('+x', '-z', …) — the thickness follows from the right hand.
 */
export const rotFor = (lx, ly) => rotFromAxes(AXV[lx], AXV[ly])

/** A rotation turned further about a world axis (degrees). */
export function rotTurn(rot, axis, angle) {
  const q0 = new Quaternion().setFromEuler(new Euler(rad(rot[0]), rad(rot[1]), rad(rot[2]), 'XYZ'))
  const q = new Quaternion().setFromAxisAngle(new Vector3(...(AXV[axis] || axis)).normalize(), rad(angle)).multiply(q0)
  const e = new Euler().setFromQuaternion(q, 'XYZ')
  return [r3(e.x), r3(e.y), r3(e.z)]
}

/** World direction of a part's local axis (0 = length, 1 = width, 2 = thickness). */
export function axisDir(rot, i) {
  const q = new Quaternion().setFromEuler(new Euler(rad(rot[0]), rad(rot[1]), rad(rot[2]), 'XYZ'))
  return AXES[i].clone().applyQuaternion(q)
}
